import React, { Suspense, lazy, useState, useEffect, useCallback, useMemo, memo } from 'react';
import {
  View,
  TouchableOpacity,
  ScrollView,
  StatusBar,
} from 'react-native';
import { Text } from '../components/ui/AppText';
import { Image } from 'expo-image';
import Svg, { Defs, LinearGradient as SvgLinearGradient, Path, Stop } from 'react-native-svg';
// React Query - Migração Fase 3
import { useHomeStats } from '../hooks/queries/useHomeQuery';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useQuestions } from '../contexts/QuestionsContext';
import DottedBackground from '../components/DottedBackground';
import { LinearGradient } from 'expo-linear-gradient';
import GameModesSection from '../components/Home/GameModesSection';
import HomeHeaderStats from '../components/Home/HomeHeaderStats';
import InviteFriendsCard from '../components/Home/InviteFriendsCard';
import BottomTabBar from '../components/BottomTabBar/BottomTabBar';
import Header from '../components/Header';
import RedacaoEntryCard from '../components/Home/RedacaoEntryCard';
import { styles } from './HomeScreen.styles';

// O modal de suporte importa audio e uma arvore grande de chat. Ele ficava no
// bundle eager da Home mesmo fechado, trazendo expo-av e seu trabalho de
// inicializacao para toda abertura da aplicacao (inclusive Perfil). Baixa e
// avalia apenas quando o usuario realmente toca em Suporte.
const SupportQuickView = lazy(() => import('../components/SupportQuickView'));

const RESOLVER_TILE_WIDTH = 130;

// Corretor de Redação OCULTO da Home (decisão do usuário, 2026-08-30: "no momento
// não vamos usar ele"). Mesmo padrão de `SHOW_CASINO_GAMES` no GamingScreen:
// esconder, não apagar. O card, as telas, os services e as rotas continuam
// intactos — reativar é trocar esta linha para `true`.
const SHOW_REDACAO_ENTRY = false;

// Cores fixas estilo ProfileScreen (dark mode)
const profileColors = {
  bgPrimary: '#0B0D17',
  bgSecondary: '#151821',
  bgTertiary: '#1E2029',
  bgCard: '#252832',
  textPrimary: '#E8EAED',
  textSecondary: '#A0A3B1',
  textTertiary: '#6B6E7B',
  accentPrimary: '#5B8FF9',
  accentSuccess: '#51CF66',
  accentWarning: '#FFD93D',
  accentPurple: '#9F7AEA',
  accentOrange: '#FF9F43',
  border: '#2A2D3A',
};

// Valores padrão para stats (usados enquanto carrega)
const DEFAULT_GAMIFICATION_STATS = {
  streak: { current: 0, longest: 0, lastStudyDate: null },
  xp: { total: 0, level: 1, title: 'Iniciante', icon: '🌱', progress: 0, xpToNextLevel: 100 },
  dailyChallenge: { questionsGoal: 10, questionsAnswered: 0, correctAnswers: 0, completed: false, progress: 0, remaining: 10 },
};

// Componente memoizado para evitar re-renders desnecessários
const PremiumCard = memo(({ title, subtitle, icon, iconImage, accentColor, onPress, isDarkMode, cardStyle }) => {
  const titleColor = isDarkMode ? '#E8EAED' : '#1F2937';
  const subtitleColor = isDarkMode ? '#A0A3B1' : '#4B5563';
  const arrowColor = isDarkMode ? '#6B6E7B' : '#9CA3AF';
  const bgColor = isDarkMode ? '#151821' : '#FFFFFF';
  const borderColor = isDarkMode ? '#2A2D3A' : 'rgba(0, 0, 0, 0.08)';
  // No tema claro o icone fica sobre card branco -> cor-base #03386C do cabecalho.
  // No escuro vem do accentColor (versao clara da mesma matiz, p/ ter contraste).
  const iconColor = isDarkMode ? accentColor : '#03386C';
  const iconWrapperStyle = isDarkMode
    ? { backgroundColor: accentColor + '20' }
    : styles.premiumIconWrapperLight;
  const iconImageStyle = isDarkMode ? styles.premiumIconImage : styles.premiumIconImageLight;

  return (
    <TouchableOpacity
      style={[styles.premiumCard, { backgroundColor: bgColor, borderColor: borderColor }, cardStyle]}
      onPress={onPress}
      activeOpacity={0.7}
    >
      <View style={[styles.premiumIconWrapper, iconWrapperStyle]}>
        {/* `tintColor` como PROP — ver a nota no ResolverPracticeCard. No style o
            expo-image o ignora, e o ícone sai com a cor original do PNG. */}
        {iconImage ? (
          <Image source={iconImage} style={iconImageStyle} tintColor={iconColor} contentFit="contain" />
        ) : (
          <Text style={[styles.premiumIcon, !isDarkMode && styles.premiumIconLight]}>{icon}</Text>
        )}
      </View>
      <View style={styles.premiumCardContent}>
        <Text style={[styles.premiumTitle, { color: titleColor }]}>{title}</Text>
        <Text style={[styles.premiumSubtitle, { color: subtitleColor }]}>{subtitle}</Text>
      </View>
      <Text style={[styles.premiumArrow, { color: arrowColor }]}>›</Text>
    </TouchableOpacity>
  );
});

const ResolverHeroShape = memo(({ isDarkMode }) => {
  const gradientColors = isDarkMode
    ? ['#252832', '#1E2029', '#1A1D26']
    : ['#FFFFFF', '#F7F7FB', '#FFFFFF'];
  const strokeColor = isDarkMode ? 'rgba(255,255,255,0.1)' : '#E9E9F2';
  const shapePath = 'M 155.546 0 C 169.524 0 180.985 10.856 191.241 20.354 C 198.375 26.961 207.923 31 218.413 31 L 305 31 C 321.569 31 335 44.431 335 61 L 335 280 C 335 296.569 321.569 310 305 310 L 30 310 C 13.431 310 0 296.569 0 280 L 0 30 C 0 13.431 13.431 0 30 0 L 155.546 0 Z';

  return (
    <Svg
      viewBox="0 0 335 310"
      preserveAspectRatio="none"
      style={styles.resolverHeroShape}
      pointerEvents="none"
    >
      <Defs>
        <SvgLinearGradient id="resolverHeroGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <Stop offset="0%" stopColor={gradientColors[0]} />
          <Stop offset="52%" stopColor={gradientColors[1]} />
          <Stop offset="100%" stopColor={gradientColors[2]} />
        </SvgLinearGradient>
      </Defs>
      {!isDarkMode && (
        <Path
          fill="none"
          stroke="#D7D8E6"
          strokeWidth="3"
          opacity="0.85"
          transform="translate(1.2 1.8)"
          d={shapePath}
        />
      )}
      <Path
        fill="url(#resolverHeroGradient)"
        stroke={strokeColor}
        strokeWidth="1"
        d={shapePath}
      />
    </Svg>
  );
});

const ResolverPracticeCard = memo(({ title, subtitle, iconImage, colors, onPress, disabled }) => (
  <TouchableOpacity
    style={[styles.resolverSlideCard, disabled && styles.resolverSlideCardDisabled]}
    onPress={onPress}
    disabled={disabled}
    activeOpacity={0.86}
  >
    <LinearGradient
      colors={colors}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
      style={styles.resolverSlideGradient}
    >
      <View style={styles.resolverCardGlow} />
      <View style={styles.resolverCardIconWrap}>
        {/* `tintColor` vai como PROP, não no style: no expo-image ele é prop do
              componente (Image.types: ImageProps.tintColor) e dentro do `style`
              é ignorado EM SILÊNCIO — por isso cada PNG aparecia com a cor
              original (sparkle branco, os demais escuros) em vez de todos brancos.
              Mesma armadilha do `pointerEvents` no ProfileScreen. */}
          <Image
            source={iconImage}
            style={styles.resolverCardIcon}
            tintColor="#FFFFFF"
            contentFit="contain"
          />
      </View>
      <View style={styles.resolverCardMeta}>
        <Text style={styles.resolverCardKind}>Modo</Text>
        <Text style={styles.resolverCardTitle} numberOfLines={2}>
          {title}
        </Text>
        <Text style={styles.resolverCardSubtitle} numberOfLines={1}>
          {subtitle}
        </Text>
      </View>
    </LinearGradient>
  </TouchableOpacity>
));


const HomeScreen = ({ navigation }) => {
  const { isDarkMode } = useTheme();
  const insets = useSafeAreaInsets();
  const { isAuthenticated, profile, user } = useAuth();
  const { questionCount, refresh } = useQuestions();
  const [showSupportQuickView, setShowSupportQuickView] = useState(false);
  // Altura real do BottomTabBar (absoluto) medida em runtime — o espaçador do
  // fim da lista reserva exatamente esse espaço pra nada ficar atrás da barra.
  const [tabBarHeight, setTabBarHeight] = useState(0);
  const handleTabBarLayout = useCallback((e) => {
    const h = e.nativeEvent.layout.height;
    setTabBarHeight((prev) => (Math.abs(prev - h) > 0.5 ? h : prev));
  }, []);

  // Estatísticas da Home via React Query (cache persistido). Consumidas DIRETO
  // do cache — sem espelhar em useState. Espelhar gerava um render extra
  // (query → effect → setState → render) e risco de dessincronizar do cache.
  const { data: statsData, refetch: refetchStats } = useHomeStats(user?.id, isAuthenticated);
  const repetitionCount = statsData?.repetitionCount ?? 0;
  const gamificationStats = statsData?.gamificationStats ?? DEFAULT_GAMIFICATION_STATS;
  const weekStudyDays = useMemo(() => new Set(statsData?.weekStudyDays || []), [statsData]);

  // Recarrega as questões (contexto) ao montar e ao focar; stats são refetchados no foco
  useEffect(() => {
    const rafId = requestAnimationFrame(() => {
      refresh();
    });

    const unsubscribe = navigation.addListener('focus', () => {
      refresh();
      refetchStats();
    });

    return () => {
      cancelAnimationFrame(rafId);
      unsubscribe();
    };
  }, [navigation, refresh, refetchStats]);

  // ==================== HANDLERS DE NAVEGAÇÃO MEMOIZADOS ====================
  const handleNavigateToUserAuth = useCallback(() => {
    // Se logado, vai para Profile; se não, vai para UserAuth
    navigation.navigate(isAuthenticated ? 'Profile' : 'UserAuth');
  }, [navigation, isAuthenticated]);

  const handleNavigateToCommunity = useCallback(() => {
    navigation.navigate('Community');
  }, [navigation]);

  const handleNavigateToConcursos = useCallback(() => {
    navigation.navigate('Concursos');
  }, [navigation]);

  const handleNavigateToSpacedRepetition = useCallback(() => {
    navigation.navigate('SpacedRepetition');
  }, [navigation]);

  const handleNavigateToManageQuestions = useCallback(() => {
    navigation.navigate('ManageQuestions');
  }, [navigation]);

  const handleNavigateToSelectCategory = useCallback(() => {
    navigation.navigate('SelectCategory');
  }, [navigation]);

  const handleNavigateToTimedExam = useCallback(() => {
    navigation.navigate('TimedExam');
  }, [navigation]);

  const handleNavigateToGaming = useCallback(() => {
    navigation.navigate('Gaming');
  }, [navigation]);

  const handleNavigateToStudyStreak = useCallback(() => {
    navigation.navigate('StudyStreak');
  }, [navigation]);

  const handleNavigateToCreateQuestion = useCallback(() => {
    navigation.navigate('CreateQuestion');
  }, [navigation]);

  // ==================== VALORES MEMOIZADOS ====================
  // Cores dos cards estilo ProfileScreen
  const cardBorderColor = useMemo(() =>
    isDarkMode ? profileColors.border : 'rgba(0, 0, 0, 0.08)',
    [isDarkMode]
  );

  // Estilo dos cards - memoizado para evitar recriação
  const cardStyle = useMemo(() => ({
    borderWidth: 1,
    borderColor: cardBorderColor,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: isDarkMode ? 0.3 : 0.08,
    shadowRadius: 8,
    elevation: 4,
  }), [cardBorderColor, isDarkMode]);

  // Dados dos dias da semana - memoizado
  const weekDaysData = useMemo(() => {
    const today = new Date().getDay();
    return ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'].map((day, index) => {
      const dayIndex = index; // Dom=0, Seg=1, ..., Sab=6
      const isToday = today === dayIndex;
      const hasStudied = weekStudyDays.has(dayIndex);
      return { day, dayIndex, isToday, hasStudied };
    });
  }, [weekStudyDays]);

  return (
    <DottedBackground>
      <View style={styles.container}>
        <StatusBar
          barStyle={isDarkMode ? 'light-content' : 'dark-content'}
          backgroundColor="transparent"
          translucent
        />

        <Header
          title={`Olá, ${isAuthenticated && profile?.nome ? profile.nome.split(' ')[0] : 'Estudante'}`}
          showUserSection={!isAuthenticated}
          onUserPress={handleNavigateToUserAuth}
          variant="home"
          matchTabBar
          rightContent={isAuthenticated ? (
            <HomeHeaderStats
              streak={gamificationStats?.streak?.current || 0}
              xp={gamificationStats?.xp?.total || 0}
              onPress={handleNavigateToUserAuth}
            />
          ) : undefined}
        />

        <ScrollView
          style={styles.content}
          showsVerticalScrollIndicator={false}
          removeClippedSubviews={true}
        >
          {/* Game Modes Section - Bento Grid */}
          <GameModesSection />

          {/* Cards Premium lado a lado */}
          <View style={styles.premiumCardsRow}>
            <PremiumCard
              title="Criar"
              subtitle="Flashcards/Questões"
              iconImage={require('../../assets/icons/sparkle.png')}
              accentColor="#4A9EDB"
              onPress={handleNavigateToCreateQuestion}
              isDarkMode={isDarkMode}
              cardStyle={cardStyle}
            />
            <PremiumCard
              title="Gerenciar"
              subtitle={`${questionCount} questões`}
              iconImage={require('../../assets/icons/layers.png')}
              accentColor="#4A9EDB"
              onPress={handleNavigateToManageQuestions}
              isDarkMode={isDarkMode}
              cardStyle={cardStyle}
            />
          </View>

          <View style={[styles.resolverHero, !isDarkMode && styles.resolverHeroLight]}>
            <ResolverHeroShape isDarkMode={isDarkMode} />
            <View style={styles.resolverDots} pointerEvents="none">
              <View style={[styles.resolverDot, !isDarkMode && styles.resolverDotLight, styles.resolverDotDim]} />
              <View style={[styles.resolverDot, !isDarkMode && styles.resolverDotLight, styles.resolverDotDim]} />
              <View style={[styles.resolverDot, !isDarkMode && styles.resolverDotLight]} />
              <View style={[styles.resolverDot, !isDarkMode && styles.resolverDotLight, styles.resolverDotDim]} />
            </View>

            <Text style={[styles.resolverHeroLabel, !isDarkMode && styles.resolverHeroLabelLight]}>Banco de questões</Text>
            <Text style={[styles.resolverHeroTitle, !isDarkMode && styles.resolverHeroTitleLight]}>Resolver Questões</Text>

            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              nestedScrollEnabled
              removeClippedSubviews={false}
              decelerationRate="fast"
              snapToInterval={RESOLVER_TILE_WIDTH + 10}
              snapToAlignment="start"
              style={styles.resolverSlides}
              contentContainerStyle={styles.resolverSlidesContent}
            >
              <ResolverPracticeCard
                title="Jogos"
                subtitle="Divirta-se"
                iconImage={require('../../assets/icons/sparkle.png')}
                // Paleta derivada da cor-base #E9C71F (hue 50): escuro -> ouro medio
                // -> a cor-base como acento vivo, mesmo padrao dos outros tiles.
                colors={['#221F11', '#796915', '#E9C71F']}
                onPress={handleNavigateToGaming}
              />

              <ResolverPracticeCard
                title="Selecionar categorias"
                subtitle="Escolha temas"
                iconImage={require('../../assets/icons/category.png')}
                // Paleta derivada da cor-base #03386C (hue 210, sat 95%): mesma matiz
                // em 3 luminosidades — canto claro, base no meio, canto profundo.
                colors={['#022445', '#03386C', '#01172D']}
                onPress={handleNavigateToSelectCategory}
                disabled={questionCount === 0}
              />

              <ResolverPracticeCard
                title="Simulado"
                subtitle="Cronometrado"
                iconImage={require('../../assets/icons/timer.png')}
                // Paleta derivada da cor-base #9AA0A6 (cinza-aco neutro): escuro ->
                // cinza medio -> a cor-base como acento, mesmo padrao dos outros tiles.
                colors={['#1B1D21', '#4A4F57', '#9AA0A6']}
                onPress={handleNavigateToTimedExam}
                disabled={questionCount === 0}
              />

              <ResolverPracticeCard
                title="Repetição"
                subtitle={`${repetitionCount} para revisar`}
                iconImage={require('../../assets/icons/brain-refresh.png')}
                colors={['#151728', '#442679', '#8B5CF6']}
                onPress={handleNavigateToSpacedRepetition}
                disabled={questionCount === 0}
              />

              <ResolverPracticeCard
                title="Concursos"
                subtitle="Por banca"
                iconImage={require('../../assets/icons/list.png')}
                colors={['#102033', '#144A73', '#3B82F6']}
                onPress={handleNavigateToConcursos}
              />
            </ScrollView>
          </View>

          {/* Card Corretor de Redação — oculto por `SHOW_REDACAO_ENTRY` (ver topo) */}
          {SHOW_REDACAO_ENTRY && <RedacaoEntryCard />}

          {/* Seção Estudo Contínuo */}
          <View style={styles.studySection}>
            <View style={styles.studySectionHeader}>
              <Text style={[styles.studyContinuousTitle, { color: isDarkMode ? '#FFFFFF' : '#1F2937' }]}>
                Estudo contínuo
              </Text>
              <TouchableOpacity onPress={handleNavigateToStudyStreak}>
                <Text style={styles.studySectionLink}>Ver detalhes</Text>
              </TouchableOpacity>
            </View>

            {/* Cards de streak e descansos */}
            <View style={[styles.studyCard, { backgroundColor: isDarkMode ? '#1E2029' : '#F3F4F6', borderColor: isDarkMode ? '#2A2D3A' : '#E5E7EB' }]}>
              <View style={styles.studyStatsRow}>
                <View style={[styles.studyStatBox, { borderColor: isDarkMode ? '#2A2D3A' : '#E5E7EB' }]}>
                  <View style={styles.studyStatRow}>
                    <View style={[styles.studyStatIcon, { backgroundColor: '#FF6B3520' }]}>
                      <Text style={{ fontSize: 12 }}>🔥</Text>
                    </View>
                    <Text style={[styles.studyStatNumber, { color: isDarkMode ? '#FFFFFF' : '#1F2937' }]}>
                      {gamificationStats?.streak?.current || 0}
                    </Text>
                  </View>
                  <Text style={[styles.studyStatLabel, { color: isDarkMode ? '#A0A3B1' : '#6B7280' }]}>
                    Dias de estudo contínuo
                  </Text>
                </View>
                <View style={[styles.studyStatBox, { borderColor: isDarkMode ? '#2A2D3A' : '#E5E7EB' }]}>
                  <View style={styles.studyStatRow}>
                    <View style={[styles.studyStatIcon, { backgroundColor: '#10B98120' }]}>
                      <Text style={{ fontSize: 12 }}>🌴</Text>
                    </View>
                    <Text style={[styles.studyStatNumber, { color: isDarkMode ? '#FFFFFF' : '#1F2937' }]}>
                      0
                    </Text>
                  </View>
                  <Text style={[styles.studyStatLabel, { color: isDarkMode ? '#A0A3B1' : '#6B7280' }]}>
                    Descansos disponíveis
                  </Text>
                </View>
              </View>

              {/* Dias da semana - usando dados memoizados */}
              <View style={[styles.weekDaysContainer, { borderColor: isDarkMode ? '#2A2D3A' : '#E5E7EB' }]}>
                {weekDaysData.map((item) => {
                  const fireOpacity = item.hasStudied ? 1 : 0.4;
                  return (
                    <View key={item.day} style={styles.weekDayItem}>
                      <Text style={{ fontSize: 14, opacity: fireOpacity }}>
                        🔥
                      </Text>
                      <Text style={{ fontSize: 10, color: item.isToday ? '#FF6B35' : (isDarkMode ? '#A0A3B1' : '#6B7280'), fontWeight: item.isToday ? '700' : '500' }}>
                        {item.day}
                      </Text>
                    </View>
                  );
                })}
              </View>

              {/* Banner de meta */}
              <View style={[styles.studyGoalBanner, { backgroundColor: '#10B98115' }]}>
                <Text style={{ fontSize: 20, marginRight: 12 }}>🌴</Text>
                <Text style={[styles.studyGoalText, { color: '#10B981' }]}>
                  Estude mais 4 dias contínuos e ganhe um dia de descanso
                </Text>
                <View style={[styles.studyGoalBadge, { marginLeft: 12 }]}>
                  <Text style={{ fontSize: 13, fontWeight: '600', color: isDarkMode ? '#A0A3B1' : '#6B7280' }}>1/5</Text>
                </View>
              </View>
            </View>
          </View>

          <InviteFriendsCard
            onPress={handleNavigateToCommunity}
            userPhoto={profile?.foto_url}
          />

          <View style={{ height: (tabBarHeight > 0 ? tabBarHeight : 80 + insets.bottom) + 16 }} />
        </ScrollView>
        <BottomTabBar
          navigation={navigation}
          currentRoute="Home"
          onSupportPress={() => setShowSupportQuickView(true)}
          onLayout={handleTabBarLayout}
        />
        {showSupportQuickView ? (
          <Suspense fallback={null}>
            <SupportQuickView
              visible
              onClose={() => setShowSupportQuickView(false)}
            />
          </Suspense>
        ) : null}
      </View>
    </DottedBackground>
  );
};

export default HomeScreen;
